import { useEffect, useRef, useState } from 'react';

const stats = [
  { value: '99.9%', label: 'Uptime' },
];

export default function AboutSection() {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);

  useEffect(() => {
    const obs1 = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setLeftVisible(true); },
      { threshold: 0.15 }
    );
    const obs2 = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setRightVisible(true); },
      { threshold: 0.15 }
    );
    if (leftRef.current) obs1.observe(leftRef.current);
    if (rightRef.current) obs2.observe(rightRef.current);
    return () => { obs1.disconnect(); obs2.disconnect(); };
  }, []);

  return (
    <section id="about" className="py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(160deg, #f8fafc 0%, #f0f9ff 100%)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          {/* Left: Image + Stats */}
          <div
            ref={leftRef}
            className="flex-1 relative"
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <div className="relative rounded-3xl overflow-hidden w-full h-80 md:h-[480px]">
              <img
                src="https://public.readdy.ai/ai/img_res/20a7ee672d58f7bc1500ed1e6531b8f8.jpg"
                alt="ELOPAR OOD team workspace"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            {/* Floating stats card */}
            <div className="absolute -bottom-6 -right-4 md:right-4 bg-white rounded-2xl p-5 flex items-center justify-center w-44 border border-gray-100">
              {stats.map((s) => (
                <div key={s.label} className="text-center">
                  <div
                    className="text-2xl font-extrabold text-gray-900"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {s.value}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Content */}
          <div
            ref={rightRef}
            className="flex-1 pt-8 lg:pt-0"
            style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
            }}
          >
            <span className="text-xs font-semibold tracking-[3px] uppercase text-sky-500 mb-3 block">
              Our Story
            </span>
            <h2
              className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              Experts in
              <br />
              iOS Development
            </h2>

            <div className="space-y-5 text-gray-600 text-base leading-relaxed">
              <p>
                <strong className="text-gray-800">ELOPAR OOD</strong> is a dedicated team of iOS specialists with a singular focus: building applications that genuinely improve people&apos;s digital lives. We combine deep technical expertise with a passion for clean, intuitive design.
              </p>
              <p>
                Our journey began with a simple belief — that mobile security and device performance should be accessible to everyone. That belief drives every product decision we make, from architecture choices to the smallest UI detail.
              </p>
              <p>
                Based in Yambol, Bulgaria, we serve users globally. Our products are trusted by tens of thousands of iOS users who rely on us to keep their devices secure, private, and running smoothly.
              </p>
            </div>

            {/* Values */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-focus-3-line', label: 'Precision Engineering' },
                { icon: 'ri-lock-password-line', label: 'Privacy First' },
                { icon: 'ri-heart-line', label: 'User Obsessed' },
                { icon: 'ri-leaf-line', label: 'Continuous Growth' },
              ].map((v) => (
                <div key={v.label} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-sky-50 flex-shrink-0">
                    <i className={`${v.icon} text-sky-500 text-base`} />
                  </div>
                  <span className="text-gray-700 text-sm font-semibold">{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
